<template>
  <div class="voyage-compare p-2">
    <page-header title="Compare Voyages">
      <template #actions>
        <b-button variant="primary" size="sm" @click="goBack">
          <i class="ri-arrow-left-line"></i> Back to Voyages
        </b-button>
      </template>
    </page-header>

    <div class="voyage-compare-pickers">
      <div v-for="key in sideKeys" :key="key" class="voyage-compare-picker" :class="`voyage-compare-picker-${key}`">
        <VoyagePicker
          :side="key.toUpperCase()"
          :other-side="otherOf(key).toUpperCase()"
          :assets="assets"
          :mass-id="sides[key].massId"
          :voyage-id="sides[key].voyageId"
          :other-voyage-id="sides[otherOf(key)].voyageId"
          :current-voyage="masterOf(key)"
          @asset-change="onAssetChange(key, $event)"
          @voyage-change="onVoyageChange(key, $event)"
        />
        <div v-if="sides[key].loading" class="voyage-compare-side-status">
          <wave-loader size="sm">Loading voyage...</wave-loader>
        </div>
        <div v-else-if="sides[key].error" class="voyage-compare-side-status">
          <span class="text-danger">{{ sides[key].error }}</span>
          <b-button variant="outline-primary" size="sm" @click="loadSide(key)">Retry</b-button>
        </div>
      </div>
      <b-button
        variant="outline-secondary"
        size="sm"
        class="voyage-compare-swap"
        :disabled="sides.a.voyageId === null && sides.b.voyageId === null"
        @click="swapSides"
      >
        <i class="ri-arrow-left-right-line"></i> Swap
      </b-button>
    </div>

    <template v-if="comparison">
      <div class="voyage-compare-sections">
        <div class="voyage-compare-columns">
          <div class="voyage-compare-columns-label">
            <span v-if="comparison.differentVessels">Different vessels — efficiency figures are indicative.</span>
          </div>
          <div v-for="key in sideKeys" :key="key" class="voyage-compare-column">
            <span class="voyage-compare-column-chip" :style="{ background: sideColors[key] }">{{ key.toUpperCase() }}</span>
            <span class="voyage-compare-column-name" :title="titleOf(key)">{{ titleOf(key) }}</span>
            <router-link
              class="voyage-compare-column-link"
              :to="{ name: 'VoyageDetail', params: { id: masterOf(key).mvygId } }"
            >Open detail</router-link>
          </div>
          <div class="voyage-compare-columns-delta">B vs A</div>
        </div>
        <CompareMetricSection v-for="group in comparison.groups" :key="group.key" :group="group" />
        <CompareOperationMode v-if="operationMode.hasData" :model="operationMode" />
      </div>
      <section class="voyage-compare-card">
        <h5 class="voyage-compare-card-title">Comparison Chart</h5>
        <VoyageCompareCharts :voyages="compareVoyages" />
      </section>
      <section class="voyage-compare-card">
        <h5 class="voyage-compare-card-title">Comparison Map</h5>
        <VoyageCompareMap :voyages="compareVoyages" />
      </section>
    </template>
    <div v-else-if="isAnySideLoading" class="voyage-compare-skeleton" aria-busy="true">
      <span class="sr-only">Loading...</span>
    </div>
    <p v-else class="voyage-compare-empty">Pick two voyages to compare.</p>
  </div>
</template>

<script>
import axios from 'axios';
import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import { fromApiDatetime } from '@/util/apiDatetime';
import { voyageDurationSeconds } from '@/views/voyage/utils/voyageSummaryModel';
import { buildComparison, buildOperationModeComparison } from '@/views/voyage/utils/voyageCompareModel';
import { SIDE_COLORS } from '@/views/voyage/utils/voyageCompareSeries';
import VoyagePicker from '@/views/voyage/components/compare/VoyagePicker.vue';
import CompareMetricSection from '@/views/voyage/components/compare/CompareMetricSection.vue';
import CompareOperationMode from '@/views/voyage/components/compare/CompareOperationMode.vue';
import VoyageCompareCharts from '@/views/voyage/components/compare/VoyageCompareCharts.vue';
import VoyageCompareMap from '@/views/voyage/components/compare/VoyageCompareMap.vue';

const SIDE_KEYS = ['a', 'b'];
const PERIOD_FORMAT = 'DD MMM YYYY HH:mm';

const toId = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const emptySide = () => ({
  massId: null,
  voyageId: null,
  summary: null,
  loading: false,
  error: '',
  token: 0,
});

export default {
  name: 'VoyageCompare',
  components: {
    VoyagePicker,
    CompareMetricSection,
    CompareOperationMode,
    VoyageCompareCharts,
    VoyageCompareMap,
  },
  data() {
    return {
      sides: { a: emptySide(), b: emptySide() },
      sideKeys: SIDE_KEYS,
      sideColors: SIDE_COLORS,
    };
  },
  computed: {
    assets() {
      return this.$store.getters['asset/assets'] || [];
    },
    isAnySideLoading() {
      return this.sides.a.loading || this.sides.b.loading;
    },
    comparison() {
      const { a, b } = this.sides;
      if (!a.summary || !b.summary) return null;
      return buildComparison({
        a: { summary: a.summary, durationSeconds: voyageDurationSeconds(a.summary.master), periodText: this.periodOf(a.summary) },
        b: { summary: b.summary, durationSeconds: voyageDurationSeconds(b.summary.master), periodText: this.periodOf(b.summary) },
      });
    },
    operationMode() {
      return buildOperationModeComparison(this.sides.a.summary, this.sides.b.summary);
    },
    // cuma berubah kalau summary berubah, biar chart/map gak fetch ulang tiap render
    compareVoyages() {
      if (!this.comparison) return null;
      return { a: this.masterOf('a'), b: this.masterOf('b') };
    },
  },
  mounted() {
    const idA = toId(this.$route.query.a);
    const idB = toId(this.$route.query.b);
    this.sides.a.voyageId = idA;
    this.sides.b.voyageId = idB === idA ? null : idB;
    this.syncUrl();
    this.loadAssets();
    SIDE_KEYS.forEach((key) => this.loadSide(key));
  },
  methods: {
    otherOf(key) {
      return key === 'a' ? 'b' : 'a';
    },
    async loadAssets() {
      if (this.assets.length) return;
      try {
        await this.$store.dispatch('asset/getAssetsData');
      } catch (error) {
        console.error('VoyageCompare assets error:', error?.message);
        this.$toast.error('Failed to load assets');
      }
    },
    // token nempel di objek sisi, jadi aman walau sisi di-swap pas fetch jalan
    async loadSide(key) {
      const side = this.sides[key];
      side.token += 1;
      const { token } = side;
      side.summary = null;
      side.error = '';
      if (side.voyageId === null) {
        side.loading = false;
        return;
      }
      side.loading = true;
      try {
        const summary = await VoyageSummarySvc.fetchById(side.voyageId);
        if (token !== side.token) return;
        if (!summary || !summary.master) throw new Error('Voyage not found.');
        side.summary = summary;
        side.massId = summary.master.mvygMassId;
        // dari detail: asset B ikut kapal A
        const other = this.sides.a === side ? this.sides.b : this.sides.a;
        if (other.massId === null && other.voyageId === null) other.massId = summary.master.mvygMassId;
      } catch (error) {
        if (token !== side.token || axios.isCancel(error)) return;
        console.error('VoyageCompare summary error:', error?.message);
        side.error = error?.message || 'Failed to load voyage.';
      } finally {
        if (token === side.token) side.loading = false;
      }
    },
    onAssetChange(key, massId) {
      const side = this.sides[key];
      side.massId = massId;
      side.voyageId = null;
      this.syncUrl();
      this.loadSide(key);
    },
    onVoyageChange(key, voyageId) {
      this.sides[key].voyageId = toId(voyageId);
      this.syncUrl();
      this.loadSide(key);
    },
    swapSides() {
      this.sides = { a: this.sides.b, b: this.sides.a };
      this.syncUrl();
    },
    syncUrl() {
      const query = {};
      SIDE_KEYS.forEach((key) => {
        if (this.sides[key].voyageId !== null) query[key] = String(this.sides[key].voyageId);
      });
      // bukan $router.replace: beforeEach batalin semua request yang lagi jalan
      const { href } = this.$router.resolve({ name: 'VoyageCompare', query });
      window.history.replaceState(window.history.state, '', href);
    },
    masterOf(key) {
      const { summary } = this.sides[key];
      return summary ? summary.master : null;
    },
    titleOf(key) {
      const master = this.masterOf(key);
      return (master && master.mvygName) || `Voyage ${key.toUpperCase()}`;
    },
    periodOf(summary) {
      const master = summary && summary.master;
      if (!master || !master.mvygStartTime || !master.mvygEndTime) return null;
      const start = fromApiDatetime(master.mvygStartTime).format(PERIOD_FORMAT);
      const end = fromApiDatetime(master.mvygEndTime).format(PERIOD_FORMAT);
      return `${start} → ${end}`;
    },
    goBack() {
      this.$router.push({ name: 'Voyage' });
    },
  },
};
</script>

<style scoped>
.voyage-compare-card-title {
  font-size: 16.8px;
  font-weight: 600;
  color: var(--mz-text-emphasis);
  margin: 0;
}

.voyage-compare-pickers {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  grid-template-areas: 'a swap b';
  gap: 8px;
  align-items: start;
  margin-bottom: 10px;
}

.voyage-compare-picker-a {
  grid-area: a;
}

.voyage-compare-picker-b {
  grid-area: b;
}

.voyage-compare-swap {
  grid-area: swap;
  align-self: start;
}

.voyage-compare-side-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 0 30px;
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-subtle);
}

.voyage-compare-sections {
  /* dipakai header & semua baris biar kolom A/B segaris */
  --compare-columns: minmax(150px, 1fr) minmax(0, 1.3fr) minmax(0, 1.3fr) 76px;

  padding: 0 12px 6px;
  margin-bottom: 10px;
  background: var(--mz-surface);
  border: 1px solid var(--mz-border);
  border-radius: var(--mz-radius-sm);
}

.voyage-compare-columns {
  position: sticky;
  /* di bawah navbar sticky 70px */
  top: calc(70px + env(safe-area-inset-top, 0px));
  z-index: 1010;
  display: grid;
  grid-template-columns: var(--compare-columns);
  gap: 0 12px;
  align-items: center;
  padding: 8px 12px;
  margin: 0 -12px;
  background: var(--mz-surface);
  border-bottom: 1px solid var(--mz-border);
  border-radius: 8px 8px 0 0;
}

.voyage-compare-columns-label {
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-subtle);
}

.voyage-compare-column {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.voyage-compare-column-chip {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: var(--mz-on-primary);
  font-size: var(--mz-fs-sm);
  font-weight: 600;
}

.voyage-compare-column-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--mz-fs-base);
  font-weight: 600;
  color: var(--mz-text-emphasis);
}

.voyage-compare-column-link {
  flex: none;
  font-size: var(--mz-fs-sm);
}

.voyage-compare-columns-delta {
  text-align: right;
  font-size: var(--mz-fs-sm);
  font-weight: 600;
  color: var(--mz-text-subtle);
}

.voyage-compare-skeleton {
  height: 160px;
  margin-bottom: 10px;
  border-radius: var(--mz-radius-sm);
  background: linear-gradient(90deg, var(--mz-skeleton-base) 25%, var(--mz-skeleton-highlight) 37%, var(--mz-skeleton-base) 63%);
  background-size: 400% 100%;
  animation: voyage-compare-shimmer 1.4s ease infinite;
}

@keyframes voyage-compare-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.voyage-compare-empty {
  margin: 0;
  padding: 16px;
  text-align: center;
  font-size: var(--mz-fs-base);
  color: var(--mz-text-subtle);
  background: var(--mz-surface);
  border: 1px solid var(--mz-border);
  border-radius: var(--mz-radius-sm);
}

.voyage-compare-card {
  background: var(--mz-surface);
  border: 1px solid var(--mz-border);
  border-radius: var(--mz-radius-sm);
  padding: 10px 12px;
  margin-bottom: 10px;
}

.voyage-compare-card-title {
  margin: 0 0 8px;
}

@media (max-width: 767.98px) {
  .voyage-compare-pickers {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'a' 'swap' 'b';
  }

  .voyage-compare-swap {
    justify-self: center;
  }

  /* HP: header tinggal chip + nama A dan B */
  .voyage-compare-columns {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
    padding: 6px 12px;
  }

  .voyage-compare-columns-label:empty,
  .voyage-compare-columns-delta {
    display: none;
  }
}
</style>
